import { UserSummary } from "../user-summary";
import { RequestBasePageIds } from "./request-base";
import { ResponseBasePage } from "./response-base";
/**
 * Request model for retrieving user summaries with comprehensive filtering options.
 * Supports filtering by company, license, role, authorization, and various user attributes.
 * 
 * Company filtering options (mutually exclusive, first match wins):
 * - companyId: Internal users only (strict company membership)
 * - companyIdWithExternals: Internal users + external users with granted access
 * - companyIdExternalsOnly: Only external users with granted access
 * 
 * @remarks
 * Access control is enforced at the server level. Callers can only retrieve users
 * they have permission to view based on their company membership and granted access.
 */
export class RequestUserSummary extends RequestBasePageIds {

  /**
   * Primary company filter for internal users only. When set, returns only
   * users whose company membership matches this ID. Does not include external
   * users who have been granted access.
   * 
   * If activeLicenseUser is true and licenseId is not set, auto-selects the
   * most recent active license for this company.
   * 
   * Note: Non-administrative users cannot query companies other than their own.
   * 
   * @see companyIdWithExternals for internal + external users
   * @see companyIdExternalsOnly for external users only
   */
  companyId?: string | null;

  /**
   * Filters to show internal users plus external users with granted access.
   * Returns users belonging to the specified company AND users from other
   * companies who have been granted access to work within that company.
   * 
   * Caller must have appropriate access permissions to the specified company.
   * Use this when you need the full picture of who can work within a company
   * context, including consultants and partners.
   * 
   * @see companyId for internal users only
   * @see companyIdExternalsOnly for external users only
   */
  companyIdWithExternals?: string | null;

  /**
   * Filters to show only external users with granted access to a company.
   * Returns users from other companies who have been granted access to work
   * within the specified company. Does not include internal company users.
   * 
   * Caller must have appropriate access permissions to the specified company.
   * Use this when you need to see consultants and partners without internal staff.
   * 
   * @see companyId for internal users only
   * @see companyIdWithExternals for both internal and external users
   */
  companyIdExternalsOnly?: string | null;

  
  companyIdGroupRelation?: string | null;


  /**
   * Filters users assigned to a specific license. When set, only users with
   * license assignments are included. External users are exempt from this
   * filter as they maintain separate license arrangements.
   */
  licenseId?: string | null;

  /**
   * License ID used to populate the assignedToLicense flag in response items.
   * Each returned UserSummary will indicate whether that user is assigned
   * to this specific license (true/false). Does not filter the result set.
   */
  assignedToLicenseId?: string | null;

  /**
   * Filters users by role assignment. Includes users directly assigned to
   * the role, or users who inherit the role through license-wide assignments.
   */
  roleId?: string | null;

  /**
   * Authorization ID filter. Returns users who have the specified authorization
   * either directly, via role assignment, or via inherited roles.
   * Respects exclusion flags for fine-grained access control.
   */
  authId?: string | null;

  /**
   * Internal application code filter for authorization-based queries.
   * Similar to authId but matches against the application's internal code.
   */
  intAppCode?: string | null;

  /**
   * When true, returns only active users.
   * When false or null, returns users regardless of active status.
   * Defaults to true in most component usage.
   */
  activeUser?: boolean | null;

  /**
   * When true, filters to users with active license assignments.
   * Also triggers automatic license selection if licenseId is not specified,
   * selecting the most recent active license for the effective company.
   */
  activeLicenseUser?: boolean | null;

  /**
   * Name/alias prefix filter. Filters users where first name or alias
   * starts with this value (case-insensitive).
   */
  startsWith?: string | null;

  /**
   * Email prefix filter. Filters users where email starts with this value.
   */
  hasEmail?: string | null;

  /**
   * When true, includes email addresses in the response UserSummary objects.
   * When false or null, email field is returned as null for privacy.
   */
  includeEmail?: boolean | null;

  /**
   * When true, includes company name in the response UserSummary objects.
   * Automatically set to true when using companyIdWithExternals or companyIdExternalsOnly.
   */
  includeCompany?: boolean | null;

  /**
   * User group code for permission-based filtering.
   * Used with company filters to validate group-level access permissions.
   * Also filters external users by their group code assignments.
   */
  userGroupCode?: string | null;
}

/**
 * Response wrapper for user summary queries.
 * Contains paginated UserSummary results with total record count.
 */
export class ResponseUserSummary extends ResponseBasePage<UserSummary> {
}


